"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RelayClient = void 0;
const tslib_1 = require("tslib");
const ethers_1 = require("ethers");
const browser_or_node_1 = tslib_1.__importDefault(require("browser-or-node"));
const http_helpers_1 = require("./http-helpers");
const types_1 = require("./types");
const endpoints_1 = require("./endpoints");
const builder_1 = require("./builder");
const encode_1 = require("./encode");
const auth_1 = require("./auth");
const utils_1 = require("./utils");
const manual_1 = require("./manual");
const response_1 = require("./response");
class RelayClient {
    constructor(relayerUrl, chainId, signer, provider, authArgs) {
        this.relayerUrl = relayerUrl.endsWith("/") ? relayerUrl.slice(0, -1) : relayerUrl;
        this.chainId = chainId;
        this.httpClient = new http_helpers_1.HttpClient();
        // If the provider is not given, fetch the provider from the signer
        if (provider != undefined) {
            this.provider = provider;
        }
        if (signer != undefined) {
            this.signer = signer;
            if (signer.provider == undefined && this.provider == undefined) {
                throw new Error("signer must have provider attached");
            }
            if (provider == undefined) {
                this.provider = this.signer.provider;
            }
        }
        if (authArgs !== undefined) {
            this.authHandler = new auth_1.AuthHandler(this.httpClient, authArgs);
        }
    }
    getRelayAddress() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.GET_ADDRESS}`, http_helpers_1.GET);
        });
    }
    getNonce(signerAddress, signerType) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.GET_NONCE}?address=${signerAddress}&type=${signerType}`, http_helpers_1.GET);
        });
    }
    getRelayPayload(signerAddress, signerType) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.GET_RELAY_PAYLOAD}?address=${signerAddress}&type=${signerType}`, http_helpers_1.GET);
        });
    }
    getTransaction(transactionId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.GET_TRANSACTION}?id=${transactionId}`, http_helpers_1.GET);
        });
    }
    getTransactions() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.GET_TRANSACTION}s`, http_helpers_1.GET);
        });
    }
    executeProxyTransactions(txns) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.signer == undefined) {
                throw new Error("missing signer");
            }
            if (this.provider == undefined) {
                throw new Error("missing provider");
            }
            const start = Date.now();
            const from = yield this.signer.getAddress();
            const rp = yield this.getRelayPayload(from, types_1.TransactionType.PROXY);
            const args = {
                from: from,
                gasPrice: "0",
                data: encode_1.encodeProxyTransactionData(txns),
                relay: rp.address,
                nonce: rp.nonce,
            };
            const request = yield builder_1.buildProxyTransactionRequest(this.signer, this.provider, args);
            console.log(`Client side proxy request creation took: ${(Date.now() - start) / 1000} seconds`);
            const resp = yield this.submitTransaction(request);
            return new response_1.ClientRelayerTransactionResponse(resp.transactionID, resp.state, resp.transactionHash, this);
        });
    }
    executeSafeTransactions(txns) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.signer == undefined) {
                throw new Error("missing signer");
            }
            const start = Date.now();
            const from = yield this.signer.getAddress();
            // Note: goes over the network
            const noncePayload = yield this.getNonce(from, types_1.TransactionType.SAFE);
            const args = {
                transactions: txns,
                from,
                nonce: noncePayload.nonce,
                chainId: this.chainId,
            };
            const request = yield builder_1.buildSafeTransactionRequest(this.signer, args);
            console.log(`Client side safe request creation took: ${(Date.now() - start) / 1000} seconds`);
            const resp = yield this.submitTransaction(request);
            return new response_1.ClientRelayerTransactionResponse(resp.transactionID, resp.state, resp.transactionHash, this);
        });
    }
    executeManualTransactions(txns, overrides) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (overrides == undefined) {
                overrides = {};
            }
            if (this.signer == undefined) {
                throw new Error("missing signer");
            }
            return manual_1.executeManualTransactions(this.signer, txns, overrides);
        });
    }
    deploySafe() {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.signer == undefined) {
                throw new Error("missing signer");
            }
            const start = Date.now();
            const from = yield ((_a = this.signer) === null || _a === void 0 ? void 0 : _a.getAddress());
            const args = {
                from: from,
                chainId: this.chainId,
                paymentToken: ethers_1.ethers.constants.AddressZero,
                payment: "0",
                paymentReceiver: ethers_1.ethers.constants.AddressZero,
            };
            const request = yield builder_1.buildSafeCreateTransactionRequest(this.signer, args);
            console.log(`Client side deploy request creation took: ${(Date.now() - start) / 1000} seconds`);
            const resp = yield this.submitTransaction(request);
            return new response_1.ClientRelayerTransactionResponse(resp.transactionID, resp.state, resp.transactionHash, this);
        });
    }
    // Periodically polls the transaction id until it reaches a desired state
    // Returns the relayer transaction if it does each the desired state
    // Times out after 10s
    pollUntilState(transactionId, states, maxPolls, pollFrequency) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            console.log(`Waiting for transaction ${transactionId} matching states: ${states}...`);
            const maxPollCount = maxPolls != undefined ? maxPolls : 10;
            let pollFreq = 2000; // Default to polling every 2 seconds
            if (pollFrequency != undefined) {
                if (pollFrequency >= 1000) {
                    pollFreq = pollFrequency;
                }
            }
            let pollCount = 0;
            while (pollCount < maxPollCount) {
                const txns = yield this.getTransaction(transactionId);
                if (txns.length > 0) {
                    const txn = txns[0];
                    if (states.includes(txn.state)) {
                        return txn;
                    }
                }
                pollCount++;
                yield utils_1.sleep(pollFreq);
            }
            console.log(`Transaction not found or not in given states, timing out`);
        });
    }
    submitTransaction(req) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            return this.send(`${this.relayerUrl}${endpoints_1.SUBMIT_TRANSACTION}`, http_helpers_1.POST, undefined, req);
        });
    }
    send(endpoint, method, headers, data, params) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            // Explicitly inject the polymarket session cookies
            // In prod environments, this will be unnecessary as the browser will have the needed cookies
            // Needed only for local testing
            if (!browser_or_node_1.default && this.authHandler !== undefined) {
                const cookie = yield this.authHandler.getPolymarketCookies();
                if (headers != undefined) {
                    headers["Cookie"] = cookie;
                }
                else {
                    headers = { "Cookie": cookie };
                }
            }
            const resp = yield this.httpClient.send(endpoint, method, headers, data, params);
            return resp.data;
        });
    }
}
exports.RelayClient = RelayClient;
